import { useCallback, useState } from 'react'

import type { Form } from 'factorial-form'

import { useEffect } from 'modules/core/hooks/useEffect'

import { type ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import { createFieldsForms } from 'modules/custom_resources/components/CustomFields/createFieldsForms'
import type CustomFieldsCollection from 'modules/custom_resources/store/collections/customFields/fields'
import ResourceFieldsCollection from 'modules/custom_resources/store/collections/customFields/resourceFields'
import CustomFieldValuesCollection from 'modules/custom_resources/store/collections/customFields/values'
import CustomField from 'modules/custom_resources/store/models/customFields/Field'
import folders from 'modules/documents/store/collections/folders'
import type Document from 'modules/documents/store/models/Document'
import { ALL_TYPES } from 'modules/documents/store/models/Folder'
import type Employee from 'modules/employees/store/models/Employee'
import { CustomFields } from 'modules/timeoff/lib/DocumentForm'
import type Leave from 'modules/timeoff/store/models/Leave'

const getValuesCollection = async (leave: Leave): Promise<any> => {
  const isNoneInstance = leave.documents.length === 0
  const instanceId = isNoneInstance ? '0' : leave.documents[0]?.attributes.id

  const collection = new CustomFieldValuesCollection([], {
    model_ref: 'document',
    instance_id: instanceId,
  })

  if (isNoneInstance) return collection

  await collection.byUsageGroup([CustomField.UsageGroups.documents])

  return collection
}

const buildFinder = (values: CustomFieldValuesCollection) => (fieldId: string) =>
  values.find({
    field_id: +fieldId,
  })

const saveCustomFields = async (values: CustomFieldValuesCollection, forms: Form[]) => {
  const find = buildFinder(values)

  return Promise.all(
    forms.map((form) => {
      const { value, field_id: fieldId } = form.data()
      const model = find(form.get('field_id').value)

      return model
        ? model.save({
            value,
            field_id: fieldId,
          })
        : values.create({
            value,
            field_id: fieldId,
          })
    })
  )
}

const updateCustomFields = async (customFieldsForms: Form[], leave: Leave) => {
  if (customFieldsForms.every((form: Form) => !form.isDirty)) return

  leave.documents.forEach(async (document: Document) => {
    const valuesCollection = new CustomFieldValuesCollection([], {
      model_ref: 'document',
      instance_id: document.attributes.id,
    })
    await valuesCollection.byUsageGroup([CustomField.UsageGroups.documents])
    await saveCustomFields(valuesCollection, customFieldsForms)
  })
}

interface Props {
  leave: Leave
  employee: Employee | null | undefined
  contractVersions: ContractVersions
}

const useCustomFields = ({ leave, employee, contractVersions }: Props) => {
  const [customFieldsForms, setCustomFieldForms] = useState<any>([])

  const fetchData = async () => {
    await folders.fetch({
      data: {
        type: ALL_TYPES.sick_leave,
      },
    })

    const sickFolder = folders.sickFolder

    if (!sickFolder || !employee) return

    const folderId = sickFolder.attributes.id

    const resourceFieldCollection = new ResourceFieldsCollection([], {
      model_ref: 'documents-folder',
      instance_id: folderId,
    })
    await resourceFieldCollection.byUsageGroup([CustomField.UsageGroups.documents])

    const valuesCollection = await getValuesCollection(leave)

    const forms = createFieldsForms(
      resourceFieldCollection as unknown as CustomFieldsCollection,
      employee,
      contractVersions,
      valuesCollection
    )
    setCustomFieldForms(forms)
  }
  useEffect(() => {
    fetchData()
  }, [leave.id])

  const getValues = useCallback(async (): Promise<CustomFields> => {
    await updateCustomFields(customFieldsForms, leave)

    return customFieldsForms.map((form: Form) => form.data()).filter((field: any) => !!field.value)
  }, [customFieldsForms, leave.id])

  return {
    forms: customFieldsForms,
    getValues,
  }
}

export default useCustomFields
